// src/features/invoice/types/invoiceTypes.js
/**
 * ============================================
 * INVOICE MODULE - TYPES & CONSTANTS
 * ============================================
 * This file contains all type definitions, enums,
 * and constants used throughout the invoice module.
 *
 * @version 1.0.0
 * @author Hashtag Photography Team
 * @lastUpdated 2024-01-25
 * ============================================
 */

/**
 * Invoice Status Enum
 * Defines all possible states an invoice can be in
 */
export const INVOICE_STATUS = {
  DRAFT: "draft",
  SENT: "sent",
  VIEWED: "viewed",
  PAID: "paid",
  PARTIALLY_PAID: "partially_paid",
  OVERDUE: "overdue",
  CANCELLED: "cancelled",
  REFUNDED: "refunded",
};

/**
 * Invoice Status Labels
 * Human-readable labels for each status
 */
export const INVOICE_STATUS_LABELS = {
  draft: "Draft",
  sent: "Sent",
  viewed: "Viewed",
  paid: "Paid",
  partially_paid: "Partially Paid",
  overdue: "Overdue",
  cancelled: "Cancelled",
  refunded: "Refunded",
};

/**
 * Invoice Status Colors
 * Color codes for each status (used for badges, chips, etc.)
 */
export const INVOICE_STATUS_COLORS = {
  draft: "#d9d9d9",
  sent: "#1890ff",
  viewed: "#13c2c2",
  paid: "#52c41a",
  partially_paid: "#faad14",
  overdue: "#ff4d4f",
  cancelled: "#8c8c8c",
  refunded: "#722ed1",
};

/**
 * Invoice Status Icons
 * Icon names for each status
 */
export const INVOICE_STATUS_ICONS = {
  draft: "📄",
  sent: "📤",
  viewed: "👁️",
  paid: "✅",
  partially_paid: "⏳",
  overdue: "⚠️",
  cancelled: "❌",
  refunded: "🔄",
};

/**
 * Invoice Status Transitions
 * Defines which status transitions are allowed
 */
export const INVOICE_STATUS_TRANSITIONS = {
  draft: ["sent", "cancelled"],
  sent: ["viewed", "overdue", "cancelled"],
  viewed: ["paid", "partially_paid", "overdue", "cancelled"],
  paid: ["refunded"],
  partially_paid: ["paid", "overdue"],
  overdue: ["paid", "partially_paid", "cancelled"],
  cancelled: [],
  refunded: [],
};

/**
 * Invoice Discount Types
 */
export const DISCOUNT_TYPES = {
  PERCENTAGE: "percentage",
  FIXED: "fixed",
};

/**
 * Invoice Currency Options
 */
export const CURRENCY_OPTIONS = [
  { code: "USD", symbol: "$", name: "US Dollar" },
  { code: "EUR", symbol: "€", name: "Euro" },
  { code: "GBP", symbol: "£", name: "British Pound" },
  { code: "INR", symbol: "₹", name: "Indian Rupee" },
  { code: "AUD", symbol: "A$", name: "Australian Dollar" },
  { code: "CAD", symbol: "C$", name: "Canadian Dollar" },
  { code: "JPY", symbol: "¥", name: "Japanese Yen" },
  { code: "CNY", symbol: "¥", name: "Chinese Yuan" },
];

/**
 * Payment Methods
 */
export const PAYMENT_METHODS = [
  { value: "cash", label: "Cash" },
  { value: "credit_card", label: "Credit Card" },
  { value: "debit_card", label: "Debit Card" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "paypal", label: "PayPal" },
  { value: "stripe", label: "Stripe" },
  { value: "check", label: "Check" },
  { value: "other", label: "Other" },
];

/**
 * Invoice Default Values
 */
export const DEFAULT_VALUES = {
  status: INVOICE_STATUS.DRAFT,
  currency: "USD",
  taxRate: 10,
  discountType: DISCOUNT_TYPES.PERCENTAGE,
  showTax: true,
  showDiscount: true,
  showNotes: true,
  showTerms: true,
  items: [
    {
      id: "item-1",
      description: "",
      quantity: 1,
      unitPrice: 0,
      discount: 0,
      tax: 0,
      notes: "",
    },
  ],
  paymentTerms: "Net 30",
  paymentMethod: "bank_transfer",
};

/**
 * Invoice Filter Options
 */
export const FILTER_OPTIONS = [
  { key: "status", label: "Status", type: "select" },
  { key: "dateRange", label: "Date Range", type: "dateRange" },
  { key: "clientId", label: "Client", type: "select" },
  { key: "minAmount", label: "Min Amount", type: "number" },
  { key: "maxAmount", label: "Max Amount", type: "number" },
  { key: "paymentMethod", label: "Payment Method", type: "select" },
  { key: "search", label: "Search", type: "text" },
];

/**
 * Invoice Sort Options
 */
export const SORT_OPTIONS = [
  { value: "date", label: "Date" },
  { value: "total", label: "Total Amount" },
  { value: "clientName", label: "Client Name" },
  { value: "status", label: "Status" },
  { value: "dueDate", label: "Due Date" },
  { value: "paymentDate", label: "Payment Date" },
];

/**
 * Error Messages
 */
export const ERROR_MESSAGES = {
  FETCH_FAILED: "Failed to fetch invoices. Please try again.",
  CREATE_FAILED: "Failed to create invoice. Please check your inputs.",
  UPDATE_FAILED: "Failed to update invoice. Please try again.",
  DELETE_FAILED: "Failed to delete invoice. Please try again.",
  STATUS_UPDATE_FAILED: "Failed to update invoice status. Please try again.",
  SEND_FAILED: "Failed to send invoice. Please try again.",
  PAYMENT_FAILED: "Failed to record payment. Please try again.",
  INVALID_STATUS_TRANSITION:
    "Invalid status transition. Please check the status flow.",
  DUPLICATE_NUMBER:
    "Invoice number already exists. Please use a unique number.",
  OVERDUE_INVOICE: "This invoice is overdue and cannot be modified.",
  ITEMS_REQUIRED: "At least one item is required.",
  CLIENT_REQUIRED: "Client information is required.",
  DUE_DATE_PAST: "Due date must be in the future.",
  PERMISSION_DENIED: "You do not have permission to perform this action.",
};

/**
 * Success Messages
 */
export const SUCCESS_MESSAGES = {
  CREATE_SUCCESS: "Invoice created successfully!",
  UPDATE_SUCCESS: "Invoice updated successfully!",
  DELETE_SUCCESS: "Invoice deleted successfully!",
  STATUS_UPDATE_SUCCESS: "Invoice status updated successfully!",
  SEND_SUCCESS: "Invoice sent successfully!",
  PAYMENT_SUCCESS: "Payment recorded successfully!",
};

/**
 * Invoice Structure
 */
export const INVOICE_STRUCTURE = {
  // Basic Info
  id: "",
  number: "",
  title: "",
  description: "",

  // Client Info
  clientId: "",
  clientName: "",
  clientEmail: "",
  clientPhone: "",
  clientCompany: "",
  clientAddress: {
    street: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
  },

  // Dates
  date: "",
  dueDate: "",
  createdAt: "",
  updatedAt: "",
  sentAt: "",
  viewedAt: "",
  paymentDate: "",

  // Items
  items: [],
  subtotal: 0,
  discount: 0,
  discountType: "percentage",
  discountAmount: 0,
  taxRate: 0,
  taxAmount: 0,
  total: 0,

  // Payment
  paymentTerms: "",
  paymentMethod: "",
  paymentStatus: "unpaid",
  amountPaid: 0,
  balanceDue: 0,
  currency: "USD",

  // Additional
  notes: "",
  termsAndConditions: "",
  purchaseOrderNumber: "",
  projectId: "",

  // Status
  status: "draft",
  statusHistory: [],
  isOverdue: false,
  overdueDays: 0,

  // Preferences
  showTax: true,
  showDiscount: true,
  showNotes: true,
  showTerms: true,

  // Attachments
  attachments: [],
  tags: [],

  // Metadata
  createdBy: "",
  updatedBy: "",
  comments: [],
  activities: [],
  customFields: {},

  // Quotation Reference
  quotationId: "",
  quotationNumber: "",
};
